import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Exam, Books, PencilLine, User, ListChecks, Newspaper } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MCQSetup() {
    const [exams, setExams] = useState([]);
    const [examId, setExamId] = useState("");
    const [boardPicked, setBoardPicked] = useState(false);
    const [subExamId, setSubExamId] = useState("");
    const [stage, setStage] = useState(""); // prelims | mains | interview
    const [subject] = useState("All");
    const [count] = useState(10);
    const [duration] = useState(15);
    const [starting, setStarting] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const boardParam = searchParams.get("board");

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/exams");
            setExams(data);
            const preferred = data.find((e) => e.exam_id === boardParam);
            if (preferred) {
                setExamId(preferred.exam_id);
                setBoardPicked(true);
                setSubExamId("");
                setStage("");
            } else if (data.length) setExamId(data[0].exam_id);
        })();
    }, [boardParam]);

    const selectedExam = exams.find((e) => e.exam_id === examId);
    const hasStages = subExamId === "kas";

    const startPractice = async () => {
        if (starting) return;
        setStarting(true);
        try {
            const { data } = await api.post("/mcq/session", {
                exam_id: examId,
                subject: subject === "All" ? null : subject,
                count: 5,
                duration_minutes: 10,
                from_pyq: true,
                stage: stage || undefined,
            });
            navigate(`/mcq/test/${data.session_id}`, { state: data });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start test");
        } finally {
            setStarting(false);
        }
    };

    return (
        <Layout>
            <div className="mb-8 text-center">
                <h1 className="font-heading font-black text-3xl sm:text-4xl">Choose your Examination</h1>
            </div>

            {/* Widget: Exam Board */}
            <div className="bento-card mb-6">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="text-sm font-bold uppercase tracking-widest text-slate-700 text-center">1. Exam Board</div>
                    {boardPicked && (
                        <button
                            data-testid="change-board-btn"
                            onClick={() => { setBoardPicked(false); setSubExamId(""); }}
                            className="text-xs font-semibold text-[#1E3A8A] hover:underline"
                        >
                            Change
                        </button>
                    )}
                </div>
                <div className={`grid gap-3 max-w-4xl mx-auto ${boardPicked ? "sm:grid-cols-1 max-w-md" : "sm:grid-cols-3"}`}>
                    {exams
                        .filter((e) => !boardPicked || e.exam_id === examId)
                        .map((e) => (
                            <button
                                key={e.exam_id}
                                data-testid={`exam-card-${e.exam_id}`}
                                onClick={() => { setExamId(e.exam_id); setBoardPicked(true); setSubExamId(""); }}
                                className={`text-left p-4 rounded-xl border-2 transition-colors ${
                                    examId === e.exam_id ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-[#BFD3FF] hover:border-[#1E3A8A]"
                                }`}
                            >
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{e.board}</div>
                                <div className="font-heading font-semibold text-sm">{e.name}</div>
                            </button>
                        ))}
                </div>
            </div>

            {boardPicked && (
                <div className="bento-card mb-6">
                    {selectedExam?.sub_exams?.length > 0 && (
                        <>
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <div className="text-sm font-bold uppercase tracking-widest text-slate-700 text-center">2. Exam Type</div>
                                {subExamId && (
                                    <button
                                        data-testid="change-sub-exam-btn"
                                        onClick={() => { setSubExamId(""); setStage(""); }}
                                        className="text-xs font-semibold text-[#1E3A8A] hover:underline"
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                            <div className={`grid gap-3 max-w-4xl mx-auto ${subExamId ? "sm:grid-cols-1 max-w-md" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
                                {selectedExam.sub_exams
                                    .filter((se) => !subExamId || se.id === subExamId)
                                    .map((se) => (
                                        <button
                                            key={se.id}
                                            data-testid={`sub-exam-${se.id}`}
                                            onClick={() => { setSubExamId(se.id); setStage(""); }}
                                            className={`text-left p-3.5 rounded-xl border-2 transition-colors ${
                                                subExamId === se.id ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-[#BFD3FF] hover:border-[#1E3A8A]"
                                            }`}
                                        >
                                            <div className="font-heading font-semibold text-sm text-slate-900">{se.name}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{se.note}</div>
                                        </button>
                                    ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Widget: Stage (only for KAS Exams sub-exam) */}
            {boardPicked && hasStages && (
                <div className="bento-card mb-6">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <div className="text-sm font-bold uppercase tracking-widest text-slate-700 text-center">3. Exam Stage</div>
                        {stage && (
                            <button
                                data-testid="change-stage-btn"
                                onClick={() => setStage("")}
                                className="text-xs font-semibold text-[#1E3A8A] hover:underline"
                            >
                                Change
                            </button>
                        )}
                    </div>
                    <div className={`grid gap-3 max-w-3xl mx-auto ${stage ? "sm:grid-cols-1 max-w-md" : "sm:grid-cols-3"}`}>
                        {[
                            { id: "prelims", name: "Prelims", note: "Preliminary examination", Icon: Books },
                            { id: "mains", name: "Mains", note: "Written descriptive papers", Icon: PencilLine },
                            { id: "interview", name: "Interview", note: "Personality test", Icon: User, disabled: true },
                        ]
                            .filter((s) => !stage || s.id === stage)
                            .map((s) => (
                                <button
                                    key={s.id}
                                    data-testid={`stage-${s.id}`}
                                    onClick={() => !s.disabled && setStage(s.id)}
                                    disabled={s.disabled}
                                    className={`text-left p-3.5 rounded-xl border-2 transition-colors ${
                                        stage === s.id ? "border-[#1E3A8A] bg-[#1E3A8A]/5" :
                                        s.disabled ? "border-[#BFD3FF]/60 opacity-60 cursor-not-allowed" :
                                        "border-[#BFD3FF] hover:border-[#1E3A8A]"
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <s.Icon size={18} weight="duotone" className="text-[#1E3A8A]" />
                                        <div className="font-heading font-semibold text-sm text-slate-900">{s.name}</div>
                                        {s.disabled && (
                                            <span className="ml-auto text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Soon</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">{s.note}</div>
                                </button>
                            ))}
                    </div>
                </div>
            )}

            {/* Widget: Section (only when stage is Prelims or Mains) */}
            {boardPicked && hasStages && (stage === "prelims" || stage === "mains") && (
                <div className="bento-card">
                    <div className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-4 text-center">
                        4. {stage === "prelims" ? "Prelims" : "Mains"} Section
                    </div>
                    <div className="grid gap-3 max-w-5xl mx-auto sm:grid-cols-2 lg:grid-cols-4">
                        <button
                            data-testid={`section-${stage}-syllabus`}
                            onClick={() => navigate(`/syllabus?board=${examId}&stage=${stage}`)}
                            className="text-left p-4 rounded-xl border-2 border-[#BFD3FF] hover:border-[#1E3A8A] transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <ListChecks size={20} weight="duotone" className="text-[#1E3A8A]" />
                                <div className="font-heading font-semibold text-slate-900">Syllabus</div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1.5">Full paper-wise topic list</div>
                        </button>
                        <button
                            data-testid={`section-${stage}-pyq`}
                            onClick={() => navigate(`/pyq?exam_id=${examId}&stage=${stage}`)}
                            className="text-left p-4 rounded-xl border-2 border-[#BFD3FF] hover:border-[#1E3A8A] transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Books size={20} weight="duotone" className="text-[#1E3A8A]" />
                                <div className="font-heading font-semibold text-slate-900">Previous Year Questions</div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1.5">Browse archived papers &amp; keys</div>
                        </button>
                        <button
                            data-testid={`section-${stage}-practice`}
                            onClick={startPractice}
                            disabled={starting}
                            className="text-left p-4 rounded-xl border-2 border-[#BFD3FF] hover:border-[#1E3A8A] transition-colors disabled:opacity-60"
                        >
                            <div className="flex items-center gap-2">
                                <Exam size={20} weight="duotone" className="text-[#1E3A8A]" />
                                <div className="font-heading font-semibold text-slate-900">
                                    {starting ? "Preparing…" : "Practice Questions"}
                                </div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1.5">Take a timed test with fresh MCQs</div>
                        </button>
                        <button
                            data-testid={`section-${stage}-study`}
                            onClick={() => navigate(`/study?exam_id=${examId}&stage=${stage}`)}
                            className="text-left p-4 rounded-xl border-2 border-[#BFD3FF] hover:border-[#1E3A8A] transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Newspaper size={20} weight="duotone" className="text-[#1E3A8A]" />
                                <div className="font-heading font-semibold text-slate-900">Study Materials</div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1.5">Curated notes &amp; current affairs</div>
                        </button>
                    </div>
                </div>
            )}
        </Layout>
    );
}
